/**
 * When a draft schedule has been left long enough to remove.
 *
 * A draft is somebody's what-if. Left alone they pile up, each one a full copy
 * of every assignment, and the scenario picker becomes a list nobody can scan.
 * So a draft nobody has worked in for a week goes. Age is counted from the
 * last time someone worked in it, not from when it was made — a draft being
 * actively refined for two weeks is not abandoned.
 *
 * Pure: the caller supplies the scenarios and the clock, and the sweep in
 * db/queries.ts does the deleting.
 */

/** A draft unused for this many days is removed. */
export const DRAFT_MAX_AGE_DAYS = 7;

const DAY_MS = 86_400_000;

export interface DraftLike {
  scenarioId: string;
  status: "draft" | "published" | "archived";
  basedOn: string | null;
  createdAt: string;
  lastUsedAt?: string | null;
}

/** The moment the draft was last worked in: last use, else creation. */
export function lastActivity(s: Pick<DraftLike, "createdAt" | "lastUsedAt">): number {
  const used = s.lastUsedAt ? Date.parse(s.lastUsedAt) : NaN;
  const made = Date.parse(s.createdAt);
  // Whichever is later: a row touched before it was created is clock skew, not history.
  return Math.max(Number.isNaN(used) ? -Infinity : used, Number.isNaN(made) ? -Infinity : made);
}

/** Whole days until this draft is removed, or null for anything that is not a draft. Never negative. */
export function draftExpiresInDays(
  s: DraftLike, now: Date, maxAgeDays: number = DRAFT_MAX_AGE_DAYS,
): number | null {
  if (s.status !== "draft") return null;
  const at = lastActivity(s);
  if (!Number.isFinite(at)) return null;
  return Math.max(Math.ceil((at + maxAgeDays * DAY_MS - now.getTime()) / DAY_MS), 0);
}

/**
 * The drafts to remove, newest first.
 *
 * Never the live schedule, never a published or archived one. And never a draft
 * another surviving scenario was started from: removing it would orphan the
 * scenario that points at it, and a draft someone still uses is not abandoned
 * just because what it was based on is old. Newest-first so that when a stale
 * draft and the stale draft it was based on both go, the child is deleted
 * before its parent.
 */
export function staleDrafts(
  scenarios: readonly DraftLike[], now: Date, maxAgeDays: number = DRAFT_MAX_AGE_DAYS,
): string[] {
  const cutoff = now.getTime() - maxAgeDays * DAY_MS;
  const stale = new Set(
    scenarios
      .filter((s) => s.status === "draft" && lastActivity(s) < cutoff)
      .map((s) => s.scenarioId),
  );
  // A parent survives if anything that is staying names it. Iterate: keeping a
  // parent keeps it as a "stayer", which can in turn pin its own parent.
  let changed = true;
  while (changed) {
    changed = false;
    for (const s of scenarios) {
      if (s.basedOn && stale.has(s.basedOn) && !stale.has(s.scenarioId)) {
        stale.delete(s.basedOn);
        changed = true;
      }
    }
  }
  return scenarios
    .filter((s) => stale.has(s.scenarioId))
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
    .map((s) => s.scenarioId);
}
